import type { ServerErrorCode } from './error-codes.generated';

/**
 * The unified error envelope returned by ehq-server on every error response:
 * `{ code, message, details? }`. Clients branch on the stable `code`, not the
 * human-readable `message`.
 */
export interface ServerError {
  /**
   * Stable, machine-readable error code. Typed against the known registry, but
   * left open to `string` so a newer server emitting a not-yet-generated code
   * does not break compilation.
   */
  code: ServerErrorCode | (string & {});
  /** Human-readable message (always present). */
  message: string;
  /** Optional structured details (e.g. a list of validation errors). */
  details?: unknown;
}

/**
 * Extract the server error `code` from an HttpErrorResponse (or any object with
 * an `error` body), if the body carries one. Returns `undefined` for legacy
 * responses that predate the unified envelope — callers should fall back to
 * message inspection in that case during rollout.
 */
export function serverErrorCode(
  error: { error?: unknown } | null | undefined,
): string | undefined {
  const body = error?.error as Partial<ServerError> | undefined;
  return typeof body?.code === 'string' ? body.code : undefined;
}
